import type {
  DateTimePickHandler,
  OpenDateTimePickerArgs,
} from './dateTimePicker';

// web-rnw twin of ./dateTimePicker.ts (see that file's header for WHY the
// seam exists at all). The browser has no imperative date dialog, so this
// borrows the one it does have: a real <input type="date"|"time"> parked
// offscreen, opened with showPicker(), read back on `change`.
//
// The input is appended to the document rather than kept detached because
// showPicker() only works on an element that is being rendered — hence
// opacity/size rather than `display: none`, which would take it out of the
// render tree and make the call throw.

// @jackedlogs/ui compiles WITHOUT `lib: dom`, on purpose: that is what keeps
// a DOM call from silently typechecking its way into a file mobile also
// loads. So the handful of members this seam touches are declared here
// instead of opening the lib for the whole package — the cost of a wrong
// declaration is confined to this file, which only the web build resolves.
type PickerInput = {
  type: string;
  value: string;
  style: Record<string, string>;
  showPicker?: () => void;
  click: () => void;
  remove: () => void;
  addEventListener: (event: string, handler: () => void) => void;
};
declare const document: {
  createElement: (tag: 'input') => PickerInput;
  body: { appendChild: (el: PickerInput) => void };
};

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Local-time "YYYY-MM-DD", the value shape <input type="date"> expects. */
function toDateValue(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Local-time "HH:MM", 24h — the value shape <input type="time"> expects. */
function toTimeValue(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// Both parsers keep the halves of the seed the picker did NOT ask about, so
// the Date handed back differs from the seed in exactly one dimension. The
// callers then read only that dimension (withDateFrom reads the date parts,
// withTimeFrom the time parts), which is the same contract the Android
// dialog honours.
function parseDateValue(value: string, seed: Date): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m === null) return null;
  const [, y, mo, da] = m;
  const next = new Date(seed.getTime());
  next.setFullYear(Number(y), Number(mo) - 1, Number(da));
  return Number.isNaN(next.getTime()) ? null : next;
}

function parseTimeValue(value: string, seed: Date): Date | null {
  const m = /^(\d{2}):(\d{2})/.exec(value);
  if (m === null) return null;
  const [, h, mi] = m;
  const next = new Date(seed.getTime());
  next.setHours(Number(h), Number(mi), 0, 0);
  return Number.isNaN(next.getTime()) ? null : next;
}

export type {
  DateTimePickEvent,
  DateTimePickHandler,
  OpenDateTimePickerArgs,
} from './dateTimePicker';

export function openDateTimePicker({
  value,
  mode,
  onChange,
}: OpenDateTimePickerArgs): void {
  const input = document.createElement('input');
  input.type = mode === 'date' ? 'date' : 'time';
  input.value = mode === 'date' ? toDateValue(value) : toTimeValue(value);
  input.style.position = 'fixed';
  input.style.left = '0';
  input.style.top = '0';
  input.style.width = '1px';
  input.style.height = '1px';
  input.style.opacity = '0';
  input.style.pointerEvents = 'none';
  document.body.appendChild(input);

  // Exactly one outcome reaches the caller: `change` and `cancel` can both
  // fire for one interaction in some browsers, and a second call would run
  // the cross-midnight arithmetic twice.
  let settled = false;
  const settle: DateTimePickHandler = (event, picked) => {
    if (settled) return;
    settled = true;
    input.remove();
    onChange(event, picked);
  };

  input.addEventListener('change', () => {
    const picked =
      mode === 'date'
        ? parseDateValue(input.value, value)
        : parseTimeValue(input.value, value);
    // A cleared field reads as an empty value: that is a dismissal, not a
    // pick of "no date" — the card has no way to represent an absent start.
    if (picked === null) settle({ type: 'dismissed' });
    else settle({ type: 'set' }, picked);
  });
  input.addEventListener('cancel', () => settle({ type: 'dismissed' }));

  // showPicker needs user activation, which every caller has (all three run
  // from a Pressable's onPress). Where it is missing entirely the click is
  // the honest fallback: some browsers open the picker from it, and the
  // ones that don't leave the field untouched, which settles as a dismissal
  // rather than writing a wrong date.
  try {
    if (typeof input.showPicker === 'function') input.showPicker();
    else input.click();
  } catch {
    settle({ type: 'dismissed' });
  }
}
